import { useCallback, useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { createLeadSchema, type LeadListItem, type LeadStatus, type Member } from '@wrapforce/shared';
import { LEAD_SOURCES, LEAD_STATUSES, PLAN_DEFINITIONS } from '@wrapforce/shared/constants';
import { z } from 'zod';
import { LayoutGrid, List, Plus, Search, AtSign, MessageCircle, Globe, Store, Users2, Phone, PhoneIncoming, MessageSquare, HelpCircle, Car } from 'lucide-react';
import { toast } from 'sonner';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { useMembers } from '@/features/workspace/api';
import { useVehicleCatalogSuggestions } from '@/features/quotes/pricing/VehiclePicker';
import { useLocations } from '@/features/locations/api';
import { useLocationStore, ALL_LOCATIONS } from '@/stores/location';
import { useCreateLead, useLeadsColumn, useLeadsList, useUpdateLead, type LeadColumnFilters } from './api';
import { useDebounce } from '@/lib/useDebounce';
import { useIsDesktop } from '@/lib/useMediaQuery';
import { Button } from '@/components/ui/button';
import { Input, Label, FieldError } from '@/components/ui/input';
import { AutocompleteInput } from '@/components/ui/autocomplete-input';
import { Dialog } from '@/components/ui/dialog';
import { LeadDrawer } from './LeadDrawer';
import { formatLeadVehicle } from './vehicle';
import { cn } from '@/lib/cn';
import { useLocaleFormatters } from '@/lib/format';

type NewLeadFormValues = z.input<typeof createLeadSchema>;

// lucide-react dropped brand/logo icons; AtSign stands in for Instagram/social handles.
const SOURCE_ICON: Record<string, typeof AtSign> = {
  instagram: AtSign, whatsapp: MessageCircle, website: Globe, walk_in: Store, referral: Users2, phone: Phone, phone_call: PhoneIncoming, sms: MessageSquare, other: HelpCircle,
};

const STATUS_STYLE: Record<LeadStatus, string> = {
  new: 'bg-surface-2 text-ink',
  contacted: 'bg-warning/15 text-warning',
  qualified: 'bg-accent/15 text-accent-ink',
  quoted: 'bg-accent/15 text-accent-ink',
  won: 'bg-success/15 text-success',
  lost: 'bg-danger/15 text-danger',
};

const BOARD_COLUMNS: { key: LeadStatus; group: 'open' | 'closed' }[] = [
  { key: 'new', group: 'open' },
  { key: 'contacted', group: 'open' },
  { key: 'quoted', group: 'open' },
  { key: 'won', group: 'closed' },
  { key: 'lost', group: 'closed' },
];
const OPEN_COLUMNS = BOARD_COLUMNS.filter((c) => c.group === 'open');
const CLOSED_COLUMNS = BOARD_COLUMNS.filter((c) => c.group === 'closed');

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '')).toUpperCase();
}

function AssigneeBadge({ member }: { member: Member | undefined }) {
  const { t } = useTranslation('leads');
  if (!member) return <span className="truncate text-xs text-muted">{t('unassigned')}</span>;
  return (
    <div className="flex min-w-0 items-center gap-1.5" title={member.fullName}>
      <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-accent/15 text-[9.5px] font-bold text-accent-ink">
        {initials(member.fullName)}
      </span>
      <span className="truncate text-xs text-ink">{member.fullName}</span>
    </div>
  );
}

function LeadColumn({
  workspaceId,
  status,
  filters,
  memberById,
  onSelect,
  onDrop,
  onCount,
}: {
  workspaceId: string | null;
  status: LeadStatus;
  filters: LeadColumnFilters;
  memberById: Map<string, Member>;
  onSelect: (id: string) => void;
  onDrop: (leadId: string, status: LeadStatus) => void;
  onCount: (status: LeadStatus, count: number) => void;
}) {
  const { t } = useTranslation('leads');
  const { formatRelative } = useLocaleFormatters();
  // NOTE (status === 'quoted'): the server filters by a single status, so legacy retired 'qualified'
  // leads (previously folded into this column) no longer appear on the board. 'qualified' is retired,
  // so this is acceptable; those leads remain reachable via the table view's status filter or search.
  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } = useLeadsColumn(workspaceId, status, filters);
  const leads = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);

  useEffect(() => {
    onCount(status, leads.length);
  }, [status, leads.length, onCount]);

  return (
    <div
      className="flex min-w-0 flex-col rounded-[var(--radius-card)] border border-border bg-surface-2"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        const leadId = e.dataTransfer.getData('text/lead-id');
        if (leadId) onDrop(leadId, status);
      }}
    >
      <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
        <span className="text-xs font-bold uppercase tracking-wide text-muted">{t(`status.lead.${status}`)}</span>
        <span className="ml-auto text-xs text-muted">{leads.length}</span>
      </div>
      <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-2.5">
        {leads.map((lead) => {
          const Icon = SOURCE_ICON[lead.source] ?? HelpCircle;
          const contact = [lead.phone, lead.email].filter(Boolean).join(' · ');
          const vehicle = formatLeadVehicle(lead);
          return (
            <div
              key={lead.id}
              draggable
              onDragStart={(e) => e.dataTransfer.setData('text/lead-id', lead.id)}
              onClick={() => onSelect(lead.id)}
              className="flex cursor-pointer flex-col gap-1.5 rounded-[var(--radius-input)] border border-border bg-surface p-3 shadow-sm hover:border-accent"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-[13px] font-semibold text-ink">{lead.name}</span>
                <Icon size={12} className="mt-0.5 shrink-0 text-muted" />
              </div>
              {contact ? <div className="truncate text-[11px] text-muted">{contact}</div> : null}
              {vehicle ? (
                <div className="flex items-center gap-1 truncate text-[11px] font-medium text-ink">
                  <Car size={11} className="shrink-0 text-muted" /> {vehicle}
                </div>
              ) : null}
              {lead.requirement ? <p className="line-clamp-2 text-[11.5px] text-muted">{lead.requirement}</p> : null}
              <div className="mt-1 flex items-center justify-between gap-2 border-t border-border pt-1.5">
                <AssigneeBadge member={memberById.get(lead.assignedTo ?? '')} />
                <span className="shrink-0 text-[10px] text-muted">{formatRelative(new Date(lead.createdAt))}</span>
              </div>
            </div>
          );
        })}
        {hasNextPage ? (
          <button
            type="button"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="rounded-[var(--radius-input)] border border-border py-1.5 text-[11px] font-semibold text-muted hover:border-accent hover:text-ink disabled:opacity-50"
          >
            {t('loadMore')}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function LeadsPage() {
  const { t } = useTranslation('leads');
  const { formatRelative } = useLocaleFormatters();
  const { membership } = useActiveMembership();
  const workspaceId = membership?.workspaceId ?? null;
  const activeLocationId = useLocationStore((s) => s.activeLocationId);
  const gateOn = membership ? PLAN_DEFINITIONS[membership.plan].gates.multiLocation : false;
  const locationId = gateOn && activeLocationId !== ALL_LOCATIONS ? activeLocationId : undefined;
  const isDesktop = useIsDesktop();
  const [view, setView] = useState<'board' | 'table'>('board');
  // The kanban board is a desktop affordance (drag-drop needs a mouse; columns
  // crush below ~700px). On mobile we always fall back to the list view.
  const effectiveView = isDesktop ? view : 'table';
  const [newOpen, setNewOpen] = useState(false);
  const [lostLeadId, setLostLeadId] = useState<string | null>(null);
  const [selectedLead, setSelectedLead] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const leadId = searchParams.get('leadId');
    if (!leadId) return;
    setSelectedLead(leadId);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('leadId');
      return next;
    });
    // Only run on mount / when the URL first carries a leadId param.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The topbar's quick-add button lands here with ?new=1; the dialog derives
  // its open state from the param so it survives the navigation, and closing
  // strips the param again.
  const newDialogOpen = newOpen || searchParams.get('new') !== null;
  function setNewDialogOpen(open: boolean) {
    setNewOpen(open);
    if (!open && searchParams.get('new') !== null) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('new');
        return next;
      });
    }
  }

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [sourceFilter, setSourceFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [counts, setCounts] = useState<Record<string, number>>({});

  const { data: members } = useMembers(workspaceId);
  const updateLead = useUpdateLead(workspaceId);
  const memberById = new Map((members ?? []).map((m) => [m.userId, m]));

  const filters = useMemo<LeadColumnFilters>(() => ({
    source: sourceFilter || undefined,
    assignedTo: assigneeFilter || undefined,
    query: debouncedSearch.trim().length >= 2 ? debouncedSearch.trim() : undefined,
    locationId,
  }), [sourceFilter, assigneeFilter, debouncedSearch, locationId]);
  const tableFilters = useMemo(() => ({ ...filters, status: statusFilter || undefined }), [filters, statusFilter]);

  const { data: tableData, hasNextPage: tableHasNextPage, isFetchingNextPage: tableIsFetchingNextPage, fetchNextPage: tableFetchNextPage, isLoading: tableIsLoading } = useLeadsList(workspaceId, tableFilters);
  const tableLeads = tableData?.pages.flatMap((p) => p.items) ?? [];

  const handleCount = useCallback((status: LeadStatus, n: number) => {
    setCounts((prev) => (prev[status] === n ? prev : { ...prev, [status]: n }));
  }, []);

  function handleDrop(leadId: string, status: LeadStatus) {
    if (status === 'lost') {
      setLostLeadId(leadId);
      return;
    }
    updateLead.mutate({ leadId, input: { status } });
  }

  function groupCount(cols: typeof OPEN_COLUMNS): number {
    return cols.reduce((sum, col) => sum + (counts[col.key] ?? 0), 0);
  }

  return (
    <div className="flex h-full flex-col gap-4 p-6" data-tour="page-leads-board">
      <div className="flex items-center gap-3">
        <div className="hidden border border-border p-0.5 md:flex">
          <button
            type="button"
            onClick={() => setView('board')}
            className={cn('flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold', view === 'board' ? 'bg-ink text-surface' : 'text-muted')}
          >
            <LayoutGrid size={13} /> {t('view.board')}
          </button>
          <button
            type="button"
            onClick={() => setView('table')}
            className={cn('flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold', view === 'table' ? 'bg-ink text-surface' : 'text-muted')}
          >
            <List size={13} /> {t('view.table')}
          </button>
        </div>
        <Button size="sm" className="ml-auto gap-1.5" onClick={() => setNewOpen(true)}>
          <Plus size={14} /> {t('newLead')}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex w-full items-center gap-2 border border-border bg-surface px-3.5 py-2 sm:w-64">
          <Search size={14} className="text-muted" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('filters.searchPlaceholder')}
            className="w-full bg-transparent text-sm outline-none"
          />
        </div>
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className="h-10 min-w-0 flex-1 rounded-[var(--radius-input)] border border-border bg-surface px-3 text-sm sm:flex-initial"
        >
          <option value="">{t('filters.allSources')}</option>
          {LEAD_SOURCES.map((s) => (
            <option key={s} value={s}>
              {t(`source.${s}`)}
            </option>
          ))}
        </select>
        <select
          value={assigneeFilter}
          onChange={(e) => setAssigneeFilter(e.target.value)}
          className="h-10 min-w-0 flex-1 rounded-[var(--radius-input)] border border-border bg-surface px-3 text-sm sm:flex-initial"
        >
          <option value="">{t('filters.allAssignees')}</option>
          {(members ?? []).map((m) => (
            <option key={m.userId} value={m.userId}>
              {m.fullName}
            </option>
          ))}
        </select>
        {effectiveView === 'table' ? (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 min-w-0 flex-1 rounded-[var(--radius-input)] border border-border bg-surface px-3 text-sm sm:flex-initial"
          >
            <option value="">{t('filters.allStatuses')}</option>
            {LEAD_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`status.lead.${s}`)}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {effectiveView === 'board' ? (
        <div className="flex min-h-0 flex-1 gap-4 overflow-x-auto">
          <div className="flex min-w-min flex-col gap-2" style={{ flex: OPEN_COLUMNS.length }}>
            <div className="flex items-center gap-2 px-0.5">
              <span className="text-[11px] font-bold uppercase tracking-wide text-ink">{t('group.open')}</span>
              <span className="text-[11px] text-muted">{groupCount(OPEN_COLUMNS)}</span>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div className="grid flex-1 gap-3" style={{ gridTemplateColumns: `repeat(${OPEN_COLUMNS.length}, minmax(200px, 1fr))` }}>
              {OPEN_COLUMNS.map((col) => (
                <LeadColumn
                  key={col.key}
                  workspaceId={workspaceId}
                  status={col.key}
                  filters={filters}
                  memberById={memberById}
                  onSelect={setSelectedLead}
                  onDrop={handleDrop}
                  onCount={handleCount}
                />
              ))}
            </div>
          </div>
          <div className="w-px shrink-0 bg-border" />
          <div className="flex min-w-min flex-col gap-2" style={{ flex: CLOSED_COLUMNS.length }}>
            <div className="flex items-center gap-2 px-0.5">
              <span className="text-[11px] font-bold uppercase tracking-wide text-ink">{t('group.closed')}</span>
              <span className="text-[11px] text-muted">{groupCount(CLOSED_COLUMNS)}</span>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div className="grid flex-1 gap-3" style={{ gridTemplateColumns: `repeat(${CLOSED_COLUMNS.length}, minmax(200px, 1fr))` }}>
              {CLOSED_COLUMNS.map((col) => (
                <LeadColumn
                  key={col.key}
                  workspaceId={workspaceId}
                  status={col.key}
                  filters={filters}
                  memberById={memberById}
                  onSelect={setSelectedLead}
                  onDrop={handleDrop}
                  onCount={handleCount}
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <>
        {/* Mobile: tappable card rows. Tables scroll poorly on phones, so the
            same rows render as a stacked card list below md. */}
        <div className="flex flex-col gap-2 md:hidden">
          {tableLeads.map((lead) => {
            const Icon = SOURCE_ICON[lead.source] ?? HelpCircle;
            const contact = [lead.phone, lead.email].filter(Boolean).join(' · ');
            const vehicle = formatLeadVehicle(lead);
            return (
              <button
                key={lead.id}
                type="button"
                onClick={() => setSelectedLead(lead.id)}
                className="flex flex-col gap-1.5 rounded-[var(--radius-card)] border border-border bg-surface p-3.5 text-left"
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-[15px] font-semibold text-ink">{lead.name}</span>
                  <span className={cn('shrink-0 px-2 py-0.5 text-[10.5px] font-bold', STATUS_STYLE[lead.status])}>{t(`status.lead.${lead.status}`)}</span>
                </div>
                {contact ? <div className="truncate text-xs text-muted">{contact}</div> : null}
                {vehicle ? (
                  <div className="flex items-center gap-1 truncate text-xs font-medium text-ink">
                    <Car size={12} className="shrink-0 text-muted" /> {vehicle}
                  </div>
                ) : null}
                {lead.requirement ? <p className="line-clamp-2 text-xs text-muted">{lead.requirement}</p> : null}
                <div className="mt-1 flex items-center justify-between border-t border-border pt-1.5">
                  <div className="flex items-center gap-1.5 text-muted">
                    <Icon size={12} /> <span className="text-xs">{t(`source.${lead.source}`)}</span>
                  </div>
                  <span className="shrink-0 text-[11px] text-muted">{formatRelative(new Date(lead.createdAt))}</span>
                </div>
              </button>
            );
          })}
          {!tableIsLoading && tableLeads.length === 0 ? (
            <div className="rounded-[var(--radius-card)] border border-border bg-surface px-4 py-10 text-center text-sm text-muted">{t('table.empty')}</div>
          ) : null}
          {tableHasNextPage ? (
            <button
              type="button"
              onClick={() => tableFetchNextPage()}
              disabled={tableIsFetchingNextPage}
              className="min-h-safe-tap rounded-[var(--radius-card)] border border-border py-2 text-xs font-semibold text-muted disabled:opacity-50"
            >
              {t('loadMore')}
            </button>
          ) : null}
        </div>

        <div className="hidden overflow-x-auto rounded-[var(--radius-card)] border border-border bg-surface md:block">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-surface-2 text-left text-xs font-bold uppercase tracking-wide text-muted">
              <tr>
                <th className="px-4 py-2.5">{t('table.name')}</th>
                <th className="px-4 py-2.5">{t('table.source')}</th>
                <th className="px-4 py-2.5">{t('table.status')}</th>
                <th className="px-4 py-2.5">{t('table.vehicle')}</th>
                <th className="px-4 py-2.5">{t('table.requirement')}</th>
                <th className="px-4 py-2.5">{t('table.assignedTo')}</th>
                <th className="px-4 py-2.5">{t('table.created')}</th>
              </tr>
            </thead>
            <tbody>
              {tableLeads.map((lead) => {
                const Icon = SOURCE_ICON[lead.source] ?? HelpCircle;
                const contact = [lead.phone, lead.email].filter(Boolean).join(' · ');
                return (
                  <tr key={lead.id} className="cursor-pointer border-b border-border last:border-0 hover:bg-surface-2" onClick={() => setSelectedLead(lead.id)}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-ink">{lead.name}</div>
                      {contact ? <div className="text-xs text-muted">{contact}</div> : null}
                    </td>
                    <td className="px-4 py-3 text-muted">
                      <div className="flex items-center gap-1.5">
                        <Icon size={12} className="text-muted" /> {t(`source.${lead.source}`)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={cn('px-2 py-0.5 text-[10.5px] font-bold', STATUS_STYLE[lead.status])}>{t(`status.lead.${lead.status}`)}</span>
                    </td>
                    <td className="px-4 py-3 text-muted">{formatLeadVehicle(lead) || '-'}</td>
                    <td className="max-w-[280px] truncate px-4 py-3 text-muted">{lead.requirement || '-'}</td>
                    <td className="px-4 py-3">
                      <AssigneeBadge member={memberById.get(lead.assignedTo ?? '')} />
                    </td>
                    <td className="px-4 py-3 text-xs text-muted">{formatRelative(new Date(lead.createdAt))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!tableIsLoading && tableLeads.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted">{t('table.empty')}</div>
          ) : null}
          {tableHasNextPage ? (
            <div className="border-t border-border px-4 py-3 text-center">
              <button
                type="button"
                onClick={() => tableFetchNextPage()}
                disabled={tableIsFetchingNextPage}
                className="text-xs font-semibold text-muted hover:text-ink disabled:opacity-50"
              >
                {t('loadMore')}
              </button>
            </div>
          ) : null}
        </div>
        </>
      )}

      {workspaceId ? <NewLeadDialog open={newDialogOpen} onOpenChange={setNewDialogOpen} workspaceId={workspaceId} /> : null}
      {lostLeadId ? <LostReasonDialog leadId={lostLeadId} updateLead={updateLead} onClose={() => setLostLeadId(null)} /> : null}
      {selectedLead ? <LeadDrawer leadId={selectedLead} onClose={() => setSelectedLead(null)} /> : null}
    </div>
  );
}

function NewLeadDialog({ open, onOpenChange, workspaceId }: { open: boolean; onOpenChange: (v: boolean) => void; workspaceId: string }) {
  const { t } = useTranslation('leads');
  const createLead = useCreateLead(workspaceId);
  const { data: members } = useMembers(workspaceId);
  const { membership } = useActiveMembership();
  const gateOn = membership ? PLAN_DEFINITIONS[membership.plan].gates.multiLocation : false;
  const { data: locationsData } = useLocations(workspaceId);
  const activeLocations = (locationsData?.items ?? []).filter((l) => l.isActive);
  const showLocationSelect = gateOn && activeLocations.length >= 2;
  const activeLocationId = useLocationStore((s) => s.activeLocationId);
  const defaultLocationId = activeLocationId !== ALL_LOCATIONS ? activeLocationId : activeLocations[0]?.id;
  const locationDefaultedRef = useRef(false);
  const yearId = useId();
  const makeId = useId();
  const modelId = useId();
  const colorId = useId();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<NewLeadFormValues>({ resolver: zodResolver(createLeadSchema), defaultValues: { source: 'other' } });
  const { makeOptions, modelOptions } = useVehicleCatalogSuggestions(workspaceId, watch('vehicleMake') ?? '', watch('vehicleModel') ?? '');

  useEffect(() => {
    if (locationDefaultedRef.current || !showLocationSelect || !defaultLocationId) return;
    locationDefaultedRef.current = true;
    setValue('locationId', defaultLocationId);
  }, [showLocationSelect, defaultLocationId, setValue]);

  async function onSubmit(values: NewLeadFormValues) {
    await createLead.mutateAsync(createLeadSchema.parse({
      ...values,
      assignedTo: values.assignedTo || undefined,
      locationId: showLocationSelect ? values.locationId : undefined,
    }));
    toast.success(t('toast.added'));
    reset();
    locationDefaultedRef.current = false;
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t('newLeadTitle')} wide>
      <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-2 gap-x-3 gap-y-4" noValidate>
        <div className="col-span-2 flex flex-col gap-1.5 md:col-span-1">
          <Label htmlFor="name">{t('form.name')}</Label>
          <Input id="name" {...register('name')} />
          <FieldError>{errors.name?.message}</FieldError>
        </div>
        <div className="col-span-2 grid grid-cols-2 gap-3 md:col-span-1">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="phone">{t('form.phone')}</Label>
            <Input id="phone" {...register('phone')} />
            <FieldError>{errors.phone?.message}</FieldError>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="email">{t('form.email')}</Label>
            <Input id="email" type="email" {...register('email')} />
            <FieldError>{errors.email?.message}</FieldError>
          </div>
        </div>
        <div className="col-span-2 flex flex-col gap-1.5 md:col-span-1">
          <Label>{t('form.source')}</Label>
          <select {...register('source')} className="h-10 rounded-[var(--radius-input)] border border-border bg-surface px-3 text-sm">
            {LEAD_SOURCES.map((s) => (
              <option key={s} value={s}>
                {t(`source.${s}`)}
              </option>
            ))}
          </select>
          <FieldError>{errors.source?.message}</FieldError>
        </div>
        <div className="col-span-2 flex flex-col gap-1.5 md:col-span-1">
          <Label htmlFor="assignedTo">{t('form.assignedTo')}</Label>
          <select id="assignedTo" {...register('assignedTo', { setValueAs: (v) => v || undefined })} className="h-10 rounded-[var(--radius-input)] border border-border bg-surface px-3 text-sm">
            <option value="">{t('unassigned')}</option>
            {(members ?? []).map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.fullName}
              </option>
            ))}
          </select>
          <FieldError>{errors.assignedTo?.message}</FieldError>
        </div>
        {showLocationSelect ? (
          <div className="col-span-2 flex flex-col gap-1.5">
            <Label>{t('form.location')}</Label>
            <select {...register('locationId', { setValueAs: (v) => v || undefined })} className="h-10 rounded-[var(--radius-input)] border border-border bg-surface px-3 text-sm">
              {activeLocations.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
            <FieldError>{errors.locationId?.message}</FieldError>
          </div>
        ) : null}
        <div className="col-span-2 flex flex-col gap-1.5">
          <Label htmlFor="requirement">{t('form.requirement')}</Label>
          <textarea id="requirement" rows={2} {...register('requirement')} className="rounded-[var(--radius-input)] border border-border bg-surface px-3 py-2 text-sm outline-none focus-visible:border-accent" />
          <FieldError>{errors.requirement?.message}</FieldError>
        </div>
        <div className="col-span-2 grid grid-cols-2 gap-3 md:col-span-1">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={yearId}>{t('form.year')}</Label>
            <Input
              id={yearId}
              type="number"
              {...register('vehicleYear', { setValueAs: (v) => (v === '' || v === null || v === undefined ? undefined : Number(v)) })}
            />
            <FieldError>{errors.vehicleYear?.message}</FieldError>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={makeId}>{t('form.make')}</Label>
            <AutocompleteInput id={makeId} value={watch('vehicleMake') ?? ''} onChange={(v) => setValue('vehicleMake', v)} options={makeOptions} />
            <FieldError>{errors.vehicleMake?.message}</FieldError>
          </div>
        </div>
        <div className="col-span-2 grid grid-cols-2 gap-3 md:col-span-1">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={modelId}>{t('form.model')}</Label>
            <AutocompleteInput id={modelId} value={watch('vehicleModel') ?? ''} onChange={(v) => setValue('vehicleModel', v)} options={modelOptions} />
            <FieldError>{errors.vehicleModel?.message}</FieldError>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={colorId}>{t('form.color')}</Label>
            <Input id={colorId} {...register('vehicleColor')} />
            <FieldError>{errors.vehicleColor?.message}</FieldError>
          </div>
        </div>
        <Button type="submit" loading={createLead.isPending} className="col-span-2 mt-1 w-full">
          {t('addLead')}
        </Button>
      </form>
    </Dialog>
  );
}

function LostReasonDialog({ leadId, updateLead, onClose }: { leadId: string; updateLead: ReturnType<typeof useUpdateLead>; onClose: () => void }) {
  const { t } = useTranslation('leads');
  const [reason, setReason] = useState('');
  const reasonId = useId();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = reason.trim();
    if (!trimmed) return;
    await updateLead.mutateAsync({ leadId, input: { status: 'lost', lostReason: trimmed } });
    toast.success(t('toast.markedLost'));
    onClose();
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }} title={t('lostDialog.title')}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={reasonId}>{t('prompts.lostReason')}</Label>
          <textarea
            id={reasonId}
            rows={3}
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('lostDialog.placeholder')}
            className="rounded-[var(--radius-input)] border border-border bg-surface px-3 py-2 text-sm outline-none focus-visible:border-accent"
          />
        </div>
        <Button type="submit" variant="danger" loading={updateLead.isPending} disabled={!reason.trim()} className="mt-1 w-full">
          {t('lostDialog.submit')}
        </Button>
      </form>
    </Dialog>
  );
}
